// src/components/BookCover/ViewDetailsDialog.jsx
// Per-version details popup: Cover Type, Print Specifications, Design Criteria.
// Saves all changed fields when "Save & Close" is clicked.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, IconButton, MenuItem, TextField, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE } from '../../utils/api';

const COVER_TYPES = [
  { value: '', label: '— Select cover type —' },
  { value: 'softcover', label: 'Softcover' },
  { value: 'softcover_flaps', label: 'Softcover with Flaps' },
  { value: 'hardcover', label: 'Hardcover' },
  { value: 'hardcover_jacket', label: 'Hardcover with Jacket' },
  { value: 'ebook', label: 'Ebook' },
];

const PAPER_TYPES = [
  { value: '', label: '— Not set —' },
  { value: 'bond_75', label: 'Bond 75 gsm' },
  { value: 'bond_90', label: 'Bond 90 gsm' },
  { value: 'cream_offset', label: 'Cream Offset' },
  { value: 'newsprint', label: 'Newsprint' },
  { value: 'coated_gloss', label: 'Coated Gloss' },
  { value: 'coated_matte', label: 'Coated Matte' },
];

const BOOK_SIZES = [
  { value: '', label: '— Not set —' },
  { value: 'half_letter', label: 'Half Letter (5.5″ × 8.5″)' },
  { value: 'trade_6x9', label: 'Trade (6″ × 9″)' },
  { value: 'a5', label: 'A5 (148 × 210 mm)' },
  { value: 'pocket_4x7', label: 'Pocket (4.25″ × 6.875″)' },
  { value: 'us_letter', label: 'US Letter (8.5″ × 11″)' },
];

const LAMINATIONS = [
  { value: '', label: '— Not set —' },
  { value: 'none', label: 'None' },
  { value: 'gloss', label: 'Gloss' },
  { value: 'matte', label: 'Matte' },
  { value: 'soft_touch', label: 'Soft Touch' },
];

const BINDINGS = [
  { value: '', label: '— Not set —' },
  { value: 'perfect', label: 'Perfect Binding' },
  { value: 'coil', label: 'Coil Binding' },
  { value: 'wire', label: 'Wire-O Binding' },
  { value: 'saddle_stitch', label: 'Saddle Stitch' },
  { value: 'case_bound', label: 'Case Bound' },
];

export default function ViewDetailsDialog({
  open,
  onClose,
  draftId,
  version,
  isAuthor = false,
  onSaved,
}) {
  const [coverType, setCoverType] = useState('');
  const [paperType, setPaperType] = useState('');
  const [bookSize, setBookSize] = useState('');
  const [lamination, setLamination] = useState('');
  const [bindingType, setBindingType] = useState('');
  const [criteria, setCriteria] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset form whenever opened with a different version
  useEffect(() => {
    if (open && version) {
      setCoverType(version.coverType || '');
      setPaperType(version.paperType || '');
      setBookSize(version.bookSize || '');
      setLamination(version.lamination || '');
      setBindingType(version.bindingType || '');
      setCriteria(version.criteria || '');
    }
  }, [open, version]);

  if (!version) return null;

  const initial = {
    coverType: version.coverType || '',
    paperType: version.paperType || '',
    bookSize: version.bookSize || '',
    lamination: version.lamination || '',
    bindingType: version.bindingType || '',
    criteria: version.criteria || '',
  };
  const current = { coverType, paperType, bookSize, lamination, bindingType, criteria };
  const isDirty = Object.keys(initial).some(k => initial[k] !== current[k]);

  const handleSave = async () => {
    if (!isAuthor) { onClose?.(); return; }
    setSaving(true);
    try {
      const t = localStorage.getItem('rv_access_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      };
      const base = `${API_BASE}/api/drafts/${draftId}/cover/versions/${version.id}`;
      const calls = [];

      if (coverType !== initial.coverType) {
        calls.push(fetch(`${base}/cover-type`, {
          method: 'PUT', headers,
          body: JSON.stringify({ coverType: coverType || null }),
        }));
      }
      const specsChanged =
        paperType !== initial.paperType ||
        bookSize !== initial.bookSize ||
        lamination !== initial.lamination ||
        bindingType !== initial.bindingType;
      if (specsChanged) {
        calls.push(fetch(`${base}/print-specs`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            paperType: paperType || null,
            bookSize: bookSize || null,
            lamination: lamination || null,
            bindingType: bindingType || null,
          }),
        }));
      }
      if (criteria !== initial.criteria) {
        calls.push(fetch(`${base}/criteria`, {
          method: 'PUT', headers,
          body: JSON.stringify({ criteria }),
        }));
      }

      const results = await Promise.all(calls);
      const failed = results.find(r => !r.ok);
      if (failed) {
        const err = await failed.json().catch(() => ({}));
        alert(err.error || 'Failed to save details');
        setSaving(false);
        return;
      }

      onSaved?.({
        coverType: coverType || null,
        paperType: paperType || null,
        bookSize: bookSize || null,
        lamination: lamination || null,
        bindingType: bindingType || null,
        criteria,
      });
      onClose?.();
    } catch {
      alert('Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (label, value, setValue, options) => (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      disabled={!isAuthor || saving}
      size="small"
      fullWidth
    >
      {options.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </TextField>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Version Details — {version.name}
        </Typography>
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
              📐 Cover Type
            </Typography>
            {renderSelect(' ', coverType, setCoverType, COVER_TYPES)}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
              📚 Print Specifications
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              {renderSelect('Paper Type', paperType, setPaperType, PAPER_TYPES)}
              {renderSelect('Book Size', bookSize, setBookSize, BOOK_SIZES)}
              {renderSelect('Cover Lamination', lamination, setLamination, LAMINATIONS)}
              {renderSelect('Binding Type', bindingType, setBindingType, BINDINGS)}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
              📋 Design Criteria
            </Typography>
            <TextField
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              disabled={!isAuthor || saving}
              placeholder={isAuthor
                ? 'Describe your vision for the cover...'
                : 'No design criteria provided.'}
              inputProps={{ maxLength: 2000 }}
              helperText={isAuthor ? `${criteria.length} / 2000` : undefined}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !isAuthor || !isDirty}
          sx={{ bgcolor: '#5A52A3', '&:hover': { bgcolor: '#3B3478' }, minWidth: 140 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save & Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
